/**
 * The parker phone rule, as every surface that takes a parker's number applies
 * it before a request is ever sent: the tenant portal's Add / Edit parker and
 * public sign-up form, and the ops console's permit parker forms. Lives beside
 * `PhoneInput` (which does the as-you-type mask) so the three apps import one
 * rule rather than each keeping a `replace(/\D/g, '').length === 10` of their
 * own — that is how the tenant form ended up with a working button and no
 * message, and the ops form with an `alert()`.
 *
 * Client mirror of `normalizeUsPhone` in parkify-pay/api/src/utils/us_phone.ts,
 * which is the rule the Worker enforces on every route that stores a parker's
 * phone (`POST /api/tenants/me/parkers`, the public registration form, the
 * tenant roster edit, and ops permit writes). The stored form is
 * exactly TEN DIGITS — not E.164, which is what the sign-in rail stores
 * (`normalizePhoneE164`) — because every reader of a permit's vehicle rows
 * (roster search, CSV export, the ops console) already matches on that shape,
 * and the records minted before this file existed carry it. Change the stored
 * form and the roster stops finding its own parkers.
 *
 * What is accepted:
 *   - ten digits, however punctuated:        (404) 555-0142 · 404.555.0142
 *   - a leading country code, as `1` or `+1`: 1 404 555 0142 · +1 (404) 555-0142
 * Anything else — short, long, or a `+` that is not `+1` — is refused with a
 * message the manager can act on. The server refuses the same inputs with the
 * same words; this is the early copy of that gate, not a replacement for it.
 * Import by file path (`packages/account/src/parkerPhone`), as the apps do for
 * `PhoneInput`: the package index pulls in the whole account experience.
 */

/** The sentence both the form and the Worker use for a number that is not a US number. */
export const PARKER_PHONE_ERROR = 'Enter a 10-digit US phone number.'

/** Ten digits, or null. Accepts a leading `1` / `+1` and drops it. */
export function normalizeParkerPhone(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const digits = trimmed.replace(/\D/g, '')
  // A `+` is only ever ours when it is `+1`: `+44…` is a real number that the
  // US mask would otherwise read as eleven-plus digits and refuse less clearly.
  if (trimmed.startsWith('+') && !digits.startsWith('1')) return null
  if (digits.length === 10) return digits
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1)
  return null
}

/**
 * Why the field cannot be submitted, or null when it can. Empty is reported
 * separately from malformed so the inline copy says the right thing: "Required"
 * for a field never touched, the format sentence for one typed wrong.
 */
export function parkerPhoneProblem(raw: string): string | null {
  if (!raw.trim()) return 'Required.'
  return normalizeParkerPhone(raw) ? null : PARKER_PHONE_ERROR
}

/** `(404) 555-0142` for a ten-digit stored value; anything else is shown as stored. */
export function formatStoredParkerPhone(stored: string | undefined): string {
  if (!stored) return ''
  const digits = normalizeParkerPhone(stored)
  if (!digits) return stored
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}
